import { useState } from 'react';
import { Camera, Video, Phone, MessageCircle, Radio } from 'lucide-react';

const CATEGORIES = [
  { key: 'fotos', label: 'Fotos', icon: Camera },
  { key: 'videos', label: 'Videos', icon: Video },
  { key: 'video_chamada', label: 'Video Chamada', icon: Phone },
  { key: 'chat', label: 'Chat', icon: MessageCircle },
  { key: 'live', label: 'Live', icon: Radio },
];

const CategoryRating = ({ ratings = {}, onSubmit, readonly = false, compact = false }) => {
  const [values, setValues] = useState({
    fotos: ratings.fotos || 0,
    videos: ratings.videos || 0,
    video_chamada: ratings.video_chamada || 0,
    chat: ratings.chat || 0,
    live: ratings.live || 0,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  const overallAvg = () => {
    const vals = Object.values(values);
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  // Compact view for cards - just show overall score
  if (compact) {
    const avg = ratings.avg_rating || 0;
    return (
      <div className="flex items-center gap-1.5">
        <div className="flex items-center gap-1 bg-[#FFF0E5] px-2 py-0.5 rounded-full">
          <span className="text-[#FF5A00] font-bold text-sm">{avg.toFixed(1)}</span>
          <span className="text-[#FF5A00]/60 text-xs">/10</span>
        </div>
        {ratings.rating_count > 0 && (
          <span className="text-[10px] md:text-xs text-slate-400">
            ({ratings.rating_count})
          </span>
        )}
      </div>
    );
  }

  // Readonly display - show category breakdown
  if (readonly) {
    return (
      <div className="space-y-3">
        {CATEGORIES.map(({ key, label, icon: Icon }) => {
          const val = ratings[`avg_${key}`] || 0;
          return (
            <div key={key} className="flex items-center gap-3">
              <div className="flex items-center gap-2 w-36 shrink-0">
                <Icon className="h-4 w-4 text-[#FF5A00]" />
                <span className="text-sm text-slate-700 font-medium">{label}</span>
              </div>
              <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#FF5A00] to-[#FF8C42] rounded-full transition-all duration-500"
                  style={{ width: `${(val / 10) * 100}%` }}
                />
              </div>
              <span className="text-sm font-bold text-slate-700 w-10 text-right">{val.toFixed(1)}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // Interactive form
  return (
    <div className="space-y-4">
      {CATEGORIES.map(({ key, label, icon: Icon }) => (
        <div key={key}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-[#FF5A00]" />
              <span className="text-sm font-medium text-slate-700">{label}</span>
            </div>
            <span className="text-sm font-bold text-[#FF5A00] min-w-[2rem] text-right">{values[key]}</span>
          </div>
          <input
            type="range"
            min="0"
            max="10"
            value={values[key]}
            onChange={(e) => handleChange(key, parseInt(e.target.value))}
            className="w-full h-2 bg-slate-100 rounded-full appearance-none cursor-pointer accent-[#FF5A00]
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 
              [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#FF5A00] [&::-webkit-slider-thumb]:shadow-md
              [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 
              [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#FF5A00] [&::-moz-range-thumb]:border-0
              [&::-moz-range-thumb]:cursor-pointer"
            data-testid={`rating-slider-${key}`}
          />
          <div className="flex justify-between text-[10px] text-slate-300 mt-0.5 px-0.5">
            <span>0</span>
            <span>5</span>
            <span>10</span>
          </div>
        </div>
      ))}

      {/* Overall Average */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-200">
        <span className="text-base font-bold text-slate-900">Nota Geral</span>
        <div className="flex items-center gap-1">
          <span className="text-2xl font-bold text-[#FF5A00]">{overallAvg()}</span>
          <span className="text-sm text-slate-400">/10</span>
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full bg-[#FF5A00] text-white font-semibold py-3 rounded-full hover:bg-[#E04F00] transition-all disabled:opacity-50"
        data-testid="submit-rating-btn"
      >
        {submitting ? 'Enviando...' : 'Enviar Avaliacao'}
      </button>
    </div>
  );
};

export default CategoryRating;
